import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowRight, 
  CheckCircle2, 
  FileText, 
  BarChart3, 
  Clock,
  Shield,
  Target
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_eeacbd60-db8b-4da1-8e71-db72803ff0fd/artifacts/f4528czt_Screenshot%202024-07-25%20103325.png";

const features = [
  {
    icon: Clock,
    title: '4-6 Minutes',
    description: 'Quick assessment to complete with clients'
  },
  {
    icon: Target,
    title: 'Tailored Questions',
    description: 'Questions adapt to business type and funding requirements'
  },
  {
    icon: BarChart3,
    title: 'Instant Score',
    description: 'Generate funding readiness score immediately'
  },
  {
    icon: FileText,
    title: 'Document Checklist',
    description: 'Know exactly what documents clients need'
  },
  {
    icon: Shield,
    title: 'Lender-Ready Report',
    description: 'Export a professional PDF to share with lenders'
  },
  {
    icon: CheckCircle2,
    title: 'Action Plan',
    description: 'Clear recommendations to improve funding readiness'
  }
];

const steps = [
  { number: '01', title: 'Client Profile', description: 'Enter client business details' },
  { number: '02', title: 'Funding Details', description: 'Define funding requirements' },
  { number: '03', title: 'Assessment', description: 'Complete tailored questions' },
  { number: '04', title: 'Generate Report', description: 'Review score and export' }
];

export default function HomePage() {
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, rgba(14, 165, 233, 0.3) 0%, transparent 50%),
                           radial-gradient(circle at 75% 75%, rgba(14, 165, 233, 0.2) 0%, transparent 50%)`
        }} />
        
        <div className="relative container-main py-20 md:py-32">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <img src={LOGO_URL} alt="EYB" className="h-14 w-14" />
                <span className="text-white/80 font-medium">EYB Consulting</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white font-heading mb-6 leading-tight">
                Client Funding
                <span className="text-sky-400"> Readiness Assessment</span>
              </h1>
              
              <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                Assess client funding readiness, identify areas that may need strengthening, 
                and generate a lender-ready preparation checklist and report.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link to={isAuthenticated ? "/assessment" : "/login"}>
                  <Button className="btn-cta text-lg h-14 px-8" data-testid="hero-cta">
                    Start Assessment
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/help">
                  <Button variant="outline" className="h-14 px-6 bg-white/10 border-white/20 text-white hover:bg-white/20">
                    Learn More
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20 bg-white">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-heading mb-4">
              How It Works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Four simple steps to assess client funding readiness
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="text-6xl font-bold text-slate-100 font-heading mb-4">
                  {step.number}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  {step.title}
                </h3>
                <p className="text-slate-600">
                  {step.description}
                </p>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-slate-100" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-slate-50">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-heading mb-4">
              What You'll Get
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Everything you need to prepare for a successful funding application
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card-default p-6 hover:shadow-lg"
              >
                <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-sky-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-600">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="container-main text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white font-heading mb-4">
              Ready to assess a client?
            </h2>
            <p className="text-lg text-slate-400 mb-8 max-w-xl mx-auto">
              Takes just 4-6 minutes. Generate a professional funding readiness report.
            </p>
            <Link to={isAuthenticated ? "/assessment" : "/login"}>
              <Button className="btn-cta text-lg h-14 px-10" data-testid="cta-start">
                Start Client Readiness Assessment
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-white border-t border-slate-200">
        <div className="container-main">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="EYB" className="h-8 w-8" />
              <span className="font-heading font-semibold text-slate-900">
                EYB Consulting
              </span>
            </div>
            <p className="text-xs text-slate-500 text-center md:text-left max-w-xl">
              EYB Consulting provides business advisory services only and does not provide credit advice. 
              Lending decisions are made solely by the lender.
            </p>
            <div className="text-sm text-slate-500">
              © {new Date().getFullYear()} EYB Consulting
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
